/**
 * Export Excel du widget "Lots par état".
 *
 * 3 feuilles :
 *  1. **Résumé** : KPI cards (nb lots, valeur totale, top famille) + doughnut
 *     répartition par famille (DLUO + NC).
 *  2. **DLUO dépassées** : liste plate, 1 ligne par stock (article × lot ×
 *     adresse) avec famille / sous-famille / qty / coût total.
 *  3. **Lots bloqués (NC)** : même structure, filtrée sur les stocks NC.
 *
 * Branding aligné sur valeur-stock-excel.utils.ts (palette + helpers
 * partagés `addJustupBranding` / `setJustupMetadata`).
 */
import * as ExcelJS from "exceljs";
import {
  Chart,
  ArcElement,
  DoughnutController,
  Legend,
  Tooltip,
} from "chart.js";
import {
  LOT_PAR_ETAT_DATA,
  LotParEtatDluoItem,
  LotParEtatNcItem,
  LotParEtatStockEntry,
} from "../../models/data.model";
import {
  addJustupBranding,
  setJustupMetadata,
} from "../../../../shared/utils/excel-branding.util";

Chart.register(ArcElement, DoughnutController, Legend, Tooltip);

const CHART_COLORS = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
];

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3",
  groupFont: "FF1B2A4A",
  articleBg: "FFF5F7FA",
  border: "FFD0D5DD",
  lotFont: "FF667085",
  lotBorder: "FFE4E7EC",
  statRed: "FFFFE6E6",
  statAmber: "FFFFF4E0",
  statBlue: "FFE6F0FF",
  statPurple: "FFF0E6FF",
  titleColor: "FF1B2A4A",
};

const UNCLASSIFIED = "— Non classé —";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers communs
// ─────────────────────────────────────────────────────────────────────────────
function renderDoughnutPng(labels: string[], values: number[]): string {
  const canvas = document.createElement("canvas");
  canvas.width = 500;
  canvas.height = 500;
  const ctx = canvas.getContext("2d")!;
  const chart = new Chart(ctx, {
    type: "doughnut",
    data: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: CHART_COLORS.slice(0, labels.length),
          borderWidth: 1,
          borderColor: "#fff",
        },
      ],
    },
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: { font: { size: 11 }, padding: 8 },
        },
        tooltip: { enabled: false },
      },
    },
  });
  chart.update();
  const png = canvas.toDataURL("image/png");
  chart.destroy();
  return png;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtEur(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return "—";
  return v.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Agrégats par famille (pour résumé + doughnut)
// ─────────────────────────────────────────────────────────────────────────────
interface FamilleAgg {
  famille: string;
  nbLots: number;
  coutTotal: number;
}

function aggregateByFamille(
  items: (LotParEtatDluoItem | LotParEtatNcItem)[],
): FamilleAgg[] {
  const map = new Map<string, FamilleAgg>();
  for (const it of items) {
    const fam = it.famille?.trim() || UNCLASSIFIED;
    const acc = map.get(fam) ?? { famille: fam, nbLots: 0, coutTotal: 0 };
    acc.nbLots += 1;
    acc.coutTotal += it.coutTotal ?? 0;
    map.set(fam, acc);
  }
  return [...map.values()].sort((a, b) => b.coutTotal - a.coutTotal);
}

// ─────────────────────────────────────────────────────────────────────────────
// Feuille Résumé
// ─────────────────────────────────────────────────────────────────────────────
async function buildResumeSheet(
  wb: ExcelJS.Workbook,
  data: LOT_PAR_ETAT_DATA,
): Promise<void> {
  const ws = wb.addWorksheet("Résumé");
  ws.columns = [{ width: 28 }, { width: 30 }, { width: 30 }, { width: 30 }];
  await addJustupBranding(wb, ws, 4);

  // ─── Titre principal ────────────────────────────────────────────────────
  ws.mergeCells("A1:D1");
  const titleCell = ws.getCell("A1");
  titleCell.value = "Lots par état — Synthèse";
  titleCell.font = { size: 16, bold: true, color: { argb: C.titleColor } };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(1).height = 28;

  // ─── KPIs ───────────────────────────────────────────────────────────────
  const ddm = data.dluo.filter((it) => it.typeDateLimite !== "DLC");
  const dlc = data.dluo.filter((it) => it.typeDateLimite === "DLC");
  const totalDdmCout = ddm.reduce((s, it) => s + (it.coutTotal ?? 0), 0);
  const totalDlcCout = dlc.reduce((s, it) => s + (it.coutTotal ?? 0), 0);
  const totalNcCout = data.nc.reduce((s, it) => s + (it.coutTotal ?? 0), 0);

  ws.mergeCells("A3:D3");
  ws.getCell("A3").value = "Indicateurs clés";
  ws.getCell("A3").font = { size: 12, bold: true, color: { argb: C.titleColor } };
  ws.getRow(3).height = 22;

  const kpiRows: Array<[string, string | number, string]> = [
    ["DLC dépassées (critique)", `${dlc.length} lots`, C.statRed],
    ["Valeur DLC immobilisée", fmtEur(totalDlcCout), C.statRed],
    ["DDM dépassées", `${ddm.length} lots`, C.statAmber],
    ["Valeur DDM immobilisée", fmtEur(totalDdmCout), C.statAmber],
    ["Lots bloqués (NC)", `${data.nc.length} lots`, C.statBlue],
    ["Valeur NC immobilisée", fmtEur(totalNcCout), C.statBlue],
  ];
  let r = 4;
  for (const [label, value, bg] of kpiRows) {
    ws.getCell(`A${r}`).value = label;
    ws.getCell(`A${r}`).font = { size: 11, color: { argb: C.titleColor } };
    ws.getCell(`A${r}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bg },
    };
    ws.getCell(`B${r}`).value = value;
    ws.getCell(`B${r}`).font = { size: 12, bold: true };
    ws.getCell(`B${r}`).alignment = { horizontal: "right" };
    ws.getCell(`B${r}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bg },
    };
    r++;
  }

  // ─── Doughnut répartition famille (DLUO + NC cumulés) ────────────────────
  const all = [...data.dluo, ...data.nc];
  const familles = aggregateByFamille(all).slice(0, 10);
  if (familles.length > 0) {
    const png = renderDoughnutPng(
      familles.map((f) => f.famille),
      familles.map((f) => f.coutTotal),
    );
    const imageId = wb.addImage({ base64: png, extension: "png" });
    ws.addImage(imageId, {
      tl: { col: 0, row: r + 1 },
      ext: { width: 380, height: 380 },
    });
    ws.getCell(`A${r + 1}`).value = "Répartition par famille (top 10)";
    ws.getCell(`A${r + 1}`).font = {
      size: 12,
      bold: true,
      color: { argb: C.titleColor },
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Feuille détail (DLUO ou NC) — 1 ligne par stock
// ─────────────────────────────────────────────────────────────────────────────
async function buildDetailSheet(
  wb: ExcelJS.Workbook,
  name: string,
  items: (LotParEtatDluoItem | LotParEtatNcItem)[],
  withDluo: boolean,
): Promise<void> {
  const ws = wb.addWorksheet(name);
  const headers = [
    "Famille",
    "Sous-famille",
    "Article",
    "Lot",
    ...(withDluo ? ["DLUO"] : []),
    "Emplacement",
    "Quantité",
    "Coût unitaire (€)",
    "Coût total (€)",
  ];
  ws.columns = headers.map((h) => ({
    header: h,
    width: Math.max(14, h.length + 4),
  }));

  // Header style
  const head = ws.getRow(1);
  head.font = { bold: true, color: { argb: C.headerFont } };
  head.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  head.alignment = { vertical: "middle", horizontal: "left" };
  head.height = 22;

  // Tri par coût total décroissant pour faire ressortir les valeurs critiques
  const sorted = [...items].sort(
    (a, b) => (b.coutTotal ?? 0) - (a.coutTotal ?? 0),
  );

  for (const it of sorted) {
    const dluoCell = withDluo
      ? fmtDate((it as LotParEtatDluoItem).dluoString)
      : null;
    for (const st of it.stocks ?? []) {
      const row: any[] = [
        it.famille?.trim() || UNCLASSIFIED,
        it.sousFamille?.trim() || UNCLASSIFIED,
        it.articleDenomination ?? "—",
        it.numeroLot ?? "—",
        ...(withDluo ? [dluoCell] : []),
        st.adresseStockDenomination ?? "—",
        st.quantite ?? 0,
        st.coutUnitaire ?? it.coutUnitaire ?? 0,
        (st.quantite ?? 0) * (st.coutUnitaire ?? it.coutUnitaire ?? 0),
      ];
      const r = ws.addRow(row);
      r.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: C.lotBorder } },
          bottom: { style: "thin", color: { argb: C.lotBorder } },
        };
      });
      // Formats numériques
      const qtyCol = withDluo ? 7 : 6;
      r.getCell(qtyCol).numFmt = "#,##0.##";
      r.getCell(qtyCol + 1).numFmt = '#,##0.00 "€"';
      r.getCell(qtyCol + 2).numFmt = '#,##0.00 "€"';
    }
  }

  ws.views = [{ state: "frozen", ySplit: 1 }];
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };
  await addJustupBranding(wb, ws, headers.length);
}

// ─────────────────────────────────────────────────────────────────────────────
// Entrée publique
// ─────────────────────────────────────────────────────────────────────────────
export async function exportLotParEtatExcel(
  data: LOT_PAR_ETAT_DATA,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, {
    title: "Lots par état",
    description:
      "DLUO dépassées et lots bloqués (zones non conformes) — extrait du dashboard Justup",
  });

  await buildResumeSheet(wb, data);

  // Split DDM / DLC en 2 feuilles distinctes (DLC = blocage légal strict
  // R. 412-21 Code Conso, DDM = avertissement qualité).
  const ddmItems = data.dluo.filter((it) => it.typeDateLimite !== "DLC");
  const dlcItems = data.dluo.filter((it) => it.typeDateLimite === "DLC");

  if (dlcItems.length > 0) {
    await buildDetailSheet(wb, "DLC dépassées (critique)", dlcItems, true);
  }
  if (ddmItems.length > 0) {
    await buildDetailSheet(wb, "DDM dépassées", ddmItems, true);
  }
  if (data.nc.length > 0) {
    await buildDetailSheet(wb, "Lots bloqués NC", data.nc, false);
  }

  const buf = await wb.xlsx.writeBuffer();
  const today = new Date().toISOString().slice(0, 10);
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `lots-par-etat-${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
